import { Component } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';

// Importaciones de entornos.
import { environment } from '../../../environments/environment';

// Importaciones de services.
import { AuthService } from '../../services/auth.service';
import { LogService } from '../../services/log.service';

// Importaciones de modelos.
import { Log } from '../../models/log.model';

/**
 * Componente de inicio de sesión.
 *
 * Este componente proporciona la interfaz de autenticación para los usuarios de la aplicación.
 * Permite ingresar credenciales, validar la autenticación y gestionar accesos.
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  standalone: false,
})
export class LoginComponent {
  /**
   * Indicador para mostrar u ocultar la contraseña en el formulario.
   * Si es verdadero, la contraseña estará oculta.
   */
  passwordHide: boolean = true;

  /**
   * Indicador del estado de carga.
   * Si es verdadero, el inicio de sesión está en proceso.
   */
  loading: boolean = false;

  /**
   * Array que almacena los mensajes de error que puedan surgir durante el inicio de sesión.
   */
  errors: string[] = [];

  /**
   * Formulario de inicio de sesión que contiene los controles para el nombre de usuario y la contraseña.
   * Este formulario se utiliza para capturar las credenciales del usuario.
   */
  form: FormGroup = new FormGroup({
    username: new FormControl(),
    password: new FormControl(),
  });

  /**
   * Obtiene el control de nombre de usuario del formulario.
   * Este método facilita el acceso al valor y estado del campo 'username'.
   */
  get formUsername(): AbstractControl | null {
    return this.form.get('username');
  }

  /**
   * Obtiene el control de contraseña del formulario.
   * Este método facilita el acceso al valor y estado del campo 'password'.
   */
  get formPassword(): AbstractControl | null {
    return this.form.get('password');
  }

  /**
   * Constructor del componente LoginComponent.
   * Inyecta el servicio de autenticación y el enrutador para gestionar la navegación.
   *
   * @param authService - Servicio de autenticación para gestionar el inicio de sesión.
   * @param logService - Servicio de registros para gestionar los accesos.
   * @param router - Servicio de enrutamiento para redirigir tras el inicio de sesión.
   */
  constructor(
    private authService: AuthService,
    private logService: LogService,
    private router: Router
  ) {}

  /**
   * Inicia el proceso de inicio de sesión del usuario enviando las credenciales al servicio de autenticación.
   * Actualiza el estado de carga mientras se procesa y gestiona los errores si la autenticación falla.
   */
  async login(): Promise<void> {
    this.loading = true;
    this.errors = [];

    try {
      await this.authService.login(
        this.formUsername?.value,
        this.formPassword?.value
      );

      // Añadir un registro de inicio de sesión.
      try {
        await this.logService.create(
          new Log({
            description: 'Ha iniciado sesión.',
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de inicio de sesión.'
          );
        }
      }

      // Redirigir al usuario después de iniciar sesión.
      this.router.navigateByUrl('/products');
    } catch (errors: any) {
      this.errors = errors;
    }

    this.loading = false;
  }
}
